import { apiEmployeeSurvey } from "@/api/employees/employee-survey.api.ts";
import { SubmitParams } from "@/components/questionnaire/questionnaire.types.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { appKeys } from "@/queries/settings/use-app.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiSurveyParticipantViewFilter } from "@/types/survey.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import { AxiosProgressEvent } from "axios";

interface EmployeeSurveyAnswerFileUploadVariables {
  employeeId: number;
  surveyId: number;
  formElementId: number;
  file: File;
  onUploadProgress?: (event: AxiosProgressEvent) => void;
}

interface EmployeeSurveyAnswerFileDeleteVariables {
  employeeId: number;
  surveyId: number;
  formElementId: number;
  fileUuid: string;
}

export const employeeSurveyKey = createQueryKeys("employeeSurvey", {
  byId: (employeeId: number, surveyId: number) => ({
    queryKey: [employeeId, surveyId],
    queryFn: async () => {
      const result = await apiEmployeeSurvey.getById(employeeId, surveyId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  formBySurvey: (employeeId: number, surveyId: number) => ({
    queryKey: [employeeId, surveyId],
    queryFn: async () => {
      const result = await apiEmployeeSurvey.getForm(employeeId, surveyId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  searchParticipantViews: (employeeId: number, filter: ApiSurveyParticipantViewFilter) => ({
    queryKey: [employeeId, filter],
  }),
});

export const useEmployeeSurveyById = ({
  employeeId,
  surveyId,
  enabled = true,
}: {
  employeeId: number;
  surveyId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    ...employeeSurveyKey.byId(employeeId, surveyId),
    enabled: enabled,
  });
};

export const useEmployeeSurveyFormBySurveyId = ({
  employeeId,
  surveyId,
  enabled = true,
}: {
  employeeId: number;
  surveyId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    ...employeeSurveyKey.formBySurvey(employeeId, surveyId),
    enabled: enabled,
  });
};

export const useSearchEmployeeSurveyParticipantViews = ({
  employeeId,
  filter,
  enabled = true,
}: {
  employeeId: number;
  filter: ApiSurveyParticipantViewFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    ...employeeSurveyKey.searchParticipantViews(employeeId, filter),
    enabled: enabled && employeeId !== undefined,
    queryFn: async ({ signal }) => {
      const result = await apiEmployeeSurvey.searchParticipantViews(employeeId, filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
  });
};

export const useEmployeeSurveyAnswerFileUpload = () => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (variables: EmployeeSurveyAnswerFileUploadVariables) => {
      const result = await apiEmployeeSurvey.uploadAnswerFile(
        variables.employeeId,
        variables.surveyId,
        variables.formElementId,
        variables.file,
        variables.onUploadProgress
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => onError(error),
  });
};

export const useEmployeeSurveyAnswerFileDelete = () => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (variables: EmployeeSurveyAnswerFileDeleteVariables) => {
      const result = await apiEmployeeSurvey.deleteAnswerFile(
        variables.employeeId,
        variables.surveyId,
        variables.formElementId,
        variables.fileUuid
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => onError(error),
  });
};

export const useEmployeeSurveySaveDraft = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ employeeId, questionnaireId, answers }: SubmitParams) {
      const result = await apiEmployeeSurvey.saveDraft(employeeId, questionnaireId, answers);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      // return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeSurveyKey.byId._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeeSurveyKey.searchParticipantViews._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeeSurveySubmitResults = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ employeeId, questionnaireId, answers }: SubmitParams) {
      const result = await apiEmployeeSurvey.submitResults(employeeId, questionnaireId, answers);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      // return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeSurveyKey.byId._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeeSurveyKey.searchParticipantViews._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
      queryClient.invalidateQueries({
        queryKey: appKeys.menuCounters.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
